// src/feature-module/settings/Dashboard/ViewUsersPieChartModule.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ApexCharts, { ApexOptions } from "apexcharts";
import apiDefinitions from "../../../core/api/apiDefinitions";
import { showError } from "../../../core/alert/sweetAlertConfig";

type User = { userType?: string };

type Props = {
  title?: string;
  height?: number;
};

const USER_TYPES = [
  "HR_MANAGER",
  "DEPARTMENT_HEAD",
  "SERVICE_MANAGER",
  "USER",
  "DRIVER",
  "MINOR_STAFF",
  "SECURITY",
];

const USER_TYPE_LABELS: Record<string, string> = {
  HR_MANAGER: "HR Manager",
  DEPARTMENT_HEAD: "Dept Head",
  SERVICE_MANAGER: "Service Mgr",
  USER: "General Staff",
  DRIVER: "Driver",
  MINOR_STAFF: "Minor Staff",
  SECURITY: "Security",
};

const MODERN_COLORS = [
  "#4F46E5", // Indigo
  "#06B6D4", // Cyan
  "#8B5CF6", // Purple
  "#10B981", // Emerald
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#64748B", // Slate
];

const ViewUsersPieChartModule: React.FC<Props> = ({
  title = "Staff Roles",
  height = 320,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<ApexCharts | null>(null);

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const all: User[] = [];
      const payload = { filterType: null, filterValue: "", date: null };
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const res = await apiDefinitions.getUsers(page, payload);
        if (res?.data?.status === "S1000") {
          const pageUsers = res.data.data?.users ?? [];
          if (!pageUsers.length) {
            hasMore = false;
            break;
          }
          const mapped = pageUsers.map((u: any) => ({
            userType: (u.group?.replace?.("_K", "") as string) || "USER",
          }));
          all.push(...mapped);
          page += 1;
        } else {
          hasMore = false;
          showError(
            "Failed to fetch users: " +
              (res?.data?.statusDescription ?? "Unknown error")
          );
        }
      }

      setUsers(all);
    } catch (e: any) {
      showError(
        "Failed to fetch users: " +
          (e?.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const { labels, series, total } = useMemo(() => {
    const counts = USER_TYPES.map(
      (t) => users.filter((u) => u.userType === t).length
    );
    const totalCount = counts.reduce((a, b) => a + b, 0);
    const formattedLabels = USER_TYPES.map((t) => USER_TYPE_LABELS[t] || t);

    return {
      labels: formattedLabels,
      series: counts,
      total: totalCount,
    };
  }, [users]);

  useEffect(() => {
    if (!chartRef.current) return;

    const options: ApexOptions = {
      chart: {
        type: "donut",
        height,
        animations: {
          enabled: true,
          dynamicAnimation: { speed: 800 },
        },
        toolbar: { show: false },
      },
      labels,
      series,
      colors: MODERN_COLORS,
      stroke: {
        width: 3,
        colors: ["#ffffff"],
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontFamily: "inherit",
        markers: { shape: "circle", size: 6 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => (val > 4 ? `${Math.round(val)}%` : ""),
      },
      tooltip: {
        y: {
          formatter: (count: number) => {
            const pct = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
            return `${count} Users (${pct}%)`;
          },
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "68%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "13px",
                fontWeight: 600,
                color: "#64748B",
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: 800,
                color: "#0F172A",
                formatter: (val: string) => String(val),
              },
              total: {
                show: true,
                label: "Total Users",
                color: "#64748B",
                fontWeight: 600,
                formatter: () => String(total || 0),
              },
            },
          },
        },
      },
      noData: {
        text: loading ? "Loading Users..." : "No User Data",
        style: { color: "#94A3B8", fontSize: "14px" },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: { height: 280 },
            legend: { position: "bottom" },
          },
        },
      ],
    };

    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    } else {
      chartInstance.current.updateOptions(options, false, true);
      chartInstance.current.updateSeries(series, true);
    }
  }, [labels, series, total, loading, height]);

  useEffect(() => {
    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, []);

  return (
    <div className="rm-glass-card rm-glass-card-interactive flex-fill w-100 p-3 p-md-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 mb-3 border-bottom border-light">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "42px",
              height: "42px",
              background: "rgba(139, 92, 246, 0.12)",
              color: "var(--rm-accent-purple)",
              fontSize: "1.25rem",
            }}
          >
            <i className="ti ti-user-check" />
          </div>
          <div>
            <h5 className="m-0 fw-bold" style={{ color: "var(--rm-text-primary)" }}>
              {title} Distribution
            </h5>
            <small style={{ color: "var(--rm-text-muted)" }}>
              {loading ? "Loading users..." : `Total Registered: ${total} Users`}
            </small>
          </div>
        </div>

        <button
          onClick={fetchUsers}
          disabled={loading}
          className="btn btn-sm btn-light rounded-pill px-3 shadow-sm border-0 d-inline-flex align-items-center gap-1"
          style={{ fontSize: "0.8rem", color: "var(--rm-text-secondary)" }}
          title="Refresh Users"
        >
          <i className={`ti ti-refresh ${loading ? "rotate" : ""}`} />
          <span className="d-none d-sm-inline">Refresh</span>
        </button>
      </div>

      <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: `${height}px` }}>
        <div id="users-distribution-chart" ref={chartRef} className="w-100" />
      </div>
    </div>
  );
};

export default ViewUsersPieChartModule;
